/**
 * What a CF-owned La Manga retreat includes.
 *
 * Extracted from `app/retreats/RetreatsClient.tsx` so the listing page and the
 * booking page state the same thing. It was inline JSX in one component before,
 * which meant putting it on the booking form would have created a second copy of
 * a nineteen-item list — and the two would have drifted the first time Niki added
 * a treatment.
 *
 * Plain data module on purpose: no JSX, no `'use client'`, no imports. Icons are
 * named rather than imported so a server component can read this file too (see
 * the server/client data boundary rule — data re-exported through a `'use client'`
 * module blows up at build time when a server component maps over it).
 */

/** Lucide icon names — consumers map these to components themselves. */
export type InclusionIcon = "CalendarCheck" | "Home" | "PlaneTakeoff";

export type RetreatHighlight = {
  icon: InclusionIcon;
  title: string;
  body: string;
};

/** The three headline inclusions, shown as cards. */
export const RETREAT_HIGHLIGHTS: RetreatHighlight[] = [
  {
    icon: "CalendarCheck",
    title: "Pre-retreat consultation",
    body: "A 1-hour 1:1 nutrition session with Niki before you travel — so the team understands your health goals from day one.",
  },
  {
    icon: "Home",
    title: "6 nights in Villa Gawy",
    body: "A 5-star beachside villa in La Manga — all rooms en suite, two private pools, balconies and free bikes.",
  },
  {
    icon: "PlaneTakeoff",
    title: "Airport transfers",
    body: "Included if you travel on the suggested flights — so you arrive as a group and the week starts together.",
  },
];

/** Everything covered by the retreat price. */
export const RETREAT_INCLUDED: string[] = [
  "1x lymphatic drainage massage",
  "Welcome bag of non-toxic toiletries and supplements worth £120",
  "Nutritionally balanced meals with local organic produce, designed by a Nutritionist",
  "3-day heavy metal liver cleanse",
  "Access to highly absorbable practitioner-strength supplements, herbs and nootropics all week",
  "All functional nutrition and integrative medicine lectures led by specialist practitioners and doctors",
  "Daily functional movement and breathwork classes",
  "Yoga classes",
  "Tai chi classes",
  "Wim Hof breathwork and ice bath experience",
  "NLP and inner confidence workshops",
  "Group hypnotherapy",
  "Hydrotherapy rehabilitation classes",
  "Cooking workshops",
  "Kinesio taping for those injured or in rehabilitation",
  "Unlimited infrared sauna, infrared mat and detoxifying foot ionisers",
  "Sea swimming and beach walks",
  "Access to padel tennis courts",
  "Relaxation time — two beaches and two private pools within the villa grounds",
];

/** 1:1 treatments bookable on arrival — deliberately NOT in the price. */
export const RETREAT_PAYABLE_EXTRAS: string[] = [
  "IV Vitamin C and glutathione drip",
  "Vitamin D and B12 injections (if required from test results)",
  "Shiatsu massage",
  "Ayurvedic yoga massage",
  "Additional lymphatic drainage massage",
  "G5 massage or Body Ballancer massage",
  "Sacrocranial osteopathy",
  "Deep tissue sport massage",
  "Full body relaxation massage",
];
